import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/**
 * Expert Group "slate" preset — canonical PrimeNG theme for the EG Angular fleet.
 *
 * Primary ramp is derived from the EG brand book core color #283747 (Fiord),
 * extended into a 50→950 sequence so PrimeNG semantics that resolve against
 * {primary.800/900/950} don't collapse to pure black.
 *
 * The `red` primitive is overridden with the brand Amaranth→Cardinal ramp so
 * every destructive affordance (p-button[severity="danger"], p-tag[severity="danger"],
 * validation/error states) renders the brand red (#E63946) instead of Aura's default.
 */
export const EG_SLATE_PRIMARY = {
    50: '#f3f6f9',
    100: '#e4eaf0',
    200: '#c8d4df',
    300: '#a3b6c8',
    400: '#7d96ac',
    500: '#587a9b',
    600: '#4a6885',
    700: '#3d566f',
    800: '#32465c',
    900: '#283747',
    950: '#1b2530'
} as const;

/** Amaranth (#E63946) → Cardinal (#BA202C) accent red. Used sparingly: destructive CTAs. */
export const EG_AMARANTH = {
    50: '#fdeaec',
    100: '#fbd0d4',
    200: '#f7a6ad',
    300: '#f17a84',
    400: '#fb505d',
    500: '#e63946',
    600: '#d11f2d',
    700: '#ba202c',
    800: '#971a24',
    900: '#7c1820',
    950: '#43090d'
} as const;

export const EgSlatePreset = definePreset(Aura, {
    primitive: {
        red: EG_AMARANTH
    },
    semantic: {
        primary: EG_SLATE_PRIMARY,
        focusRing: {
            // Brand focus ring is Waikawa (#597695 ≈ {primary.500}).
            width: '2px',
            style: 'solid',
            color: '{primary.500}',
            offset: '2px'
        },
        formField: {
            borderRadius: '8px'
        },
        content: {
            borderRadius: '12px'
        },
        colorScheme: {
            light: {
                primary: {
                    color: '{primary.900}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.800}',
                    activeColor: '{primary.700}'
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.700}',
                    focusColor: '{primary.800}'
                },
                // Brand-tinted neutral surfaces (50 = #F2F6FC surface-subtle,
                // 100 = #DCE5F4, 200 = #CFD9EA border) so tables, cards and
                // inputs inherit brand surfaces.
                surface: {
                    0: '#ffffff',
                    50: '#f2f6fc',
                    100: '#dce5f4',
                    200: '#cfd9ea',
                    300: '#b3c2d6',
                    400: '#808c9e',
                    500: '#5f6b7d',
                    600: '#4a5461',
                    700: '#3a434e',
                    800: '#283747',
                    900: '#1f2a36',
                    950: '#18222c'
                }
            },
            dark: {
                primary: {
                    color: '{primary.300}',
                    contrastColor: '{primary.950}',
                    hoverColor: '{primary.200}',
                    activeColor: '{primary.100}'
                },
                highlight: {
                    background: 'color-mix(in srgb, {primary.300}, transparent 84%)',
                    focusBackground: 'color-mix(in srgb, {primary.300}, transparent 76%)',
                    color: 'rgba(255,255,255,.92)',
                    focusColor: 'rgba(255,255,255,.92)'
                }
            }
        }
    }
});
